"use client"

/**
 * 10b — Stories. The reviews rail again (`home-v4-reviews.tsx`), with the
 * text review's card exactly (Eugene, 18.09): 300×436, neutral-50, radius
 * 16, the opening line at 20/24.75 Medium, the start of the story in the
 * person's own words, clamped, then who they are. The card is a link: the
 * full story lives on its own page. That page does not exist yet, so the
 * cards open /reviews for now.
 *
 * Three FICTIONAL placeholders until real, consented cases exist. Where a
 * review card shows five stars, a story card says it is a fictional example,
 * in the page's language. The quotation marks are in the catalogue, since
 * each language sets its own pair.
 */

import { useRef } from "react"
import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/ui/icons"
import { useHomeV4Copy } from "./home-v4-copy"

const CARD_W = 300
const GAP = 14

/** The review card's headline rung (20/24.75 Medium), page-scoped. */
function StoriesType() {
  return (
    <style>{`
      .hv4-story-headline{font-size:20px;line-height:24.75px;font-weight:500}
    `}</style>
  )
}

export function HomeV4Stories({ section }: { section: string }) {
  const lp = useLocalePath()
  const { STORIES } = useHomeV4Copy()
  const railRef = useRef<HTMLUListElement>(null)
  const scrollBy = (dir: -1 | 1) =>
    railRef.current?.scrollBy({
      left: dir * (CARD_W + GAP),
      behavior: "smooth",
    })

  return (
    <section className="bg-white pt-4 pb-10 md:pt-8 md:pb-[114px]">
      <StoriesType />
      <div className={`${section} text-center`}>
        <p className="type-label-lg text-neutral-500">{STORIES.eyebrow}</p>
        <h2 className="type-display-md-emphasized mx-auto mt-6 max-w-[664px] text-neutral-900">
          {STORIES.heading}
        </h2>
      </div>

      <ul
        ref={railRef}
        className="mt-12 flex snap-x [scrollbar-width:none] gap-[14px] overflow-x-auto pr-6 pb-1 pl-6 md:mt-[72px] md:pl-[max(2.5rem,calc((100vw-1440px)/2+2.5rem))] [&::-webkit-scrollbar]:hidden"
      >
        {STORIES.stories.map((story) => (
          <li
            key={story.id}
            className="h-[436px] w-[300px] shrink-0 snap-start"
          >
            <Link
              href={lp("/reviews")}
              className="flex h-full flex-col rounded-2xl bg-neutral-50 px-8 pt-[29px] pb-8 transition-colors hover:bg-neutral-100"
            >
              <div className="flex min-h-0 flex-col gap-4">
                <p className="hv4-story-headline text-neutral-900">
                  {story.headline}
                </p>
                <p className="type-body-xl line-clamp-6 text-neutral-950">
                  {story.body}
                </p>
              </div>
              <figure className="mt-auto flex items-center gap-3 pt-[26px]">
                <span
                  className="type-body-lg-emphasized flex size-10 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-neutral-700"
                  aria-hidden
                >
                  {story.name.charAt(0)}
                </span>
                <figcaption>
                  <p className="type-body-xl text-neutral-900">{story.name}</p>
                  <p className="type-body-md mt-0.5 text-neutral-500">
                    {STORIES.fictional}
                  </p>
                </figcaption>
              </figure>
            </Link>
          </li>
        ))}
      </ul>

      <div
        className={`${section} mt-6 flex items-center justify-end md:mt-[22px]`}
      >
        <div className="hidden gap-2 md:flex">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Previous stories"
            className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-neutral-50 text-neutral-900 transition-colors hover:bg-neutral-100"
          >
            <ChevronLeftIcon width={24} height={24} />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Next stories"
            className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-neutral-100 text-neutral-900 transition-colors hover:bg-neutral-200"
          >
            <ChevronRightIcon width={24} height={24} />
          </button>
        </div>
      </div>
    </section>
  )
}
